// Create a new file: utils/tokenValidation.ts
// Reusable token validation utilities

interface JWTPayload {
    exp: number;
    iat: number;
    iss: string;
    sub: string;
    [key: string]: any;
  }
  
  export const decodeJWTPayload = (token: string): JWTPayload | null => {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = parts[1];
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.log("Error decoding JWT:", error);
      return null;
    }
  };
  
  // Check if a single token is valid (format + expiry)
  export const isTokenValid = (token: string, bufferMinutes: number = 5): boolean => {
    if (!token) return false;
    
    try {
      const payload = decodeJWTPayload(token);
      if (!payload || !payload.exp) return false;
      
      const now = Math.floor(Date.now() / 1000);
      const bufferTime = bufferMinutes * 60;
      
      // Token is valid if it hasn't expired (with buffer)
      return now < (payload.exp - bufferTime);
    } catch (error) {
      console.log("Error validating token:", error);
      return false;
    }
  };
  
  // Main function: Validate all stored authentication tokens
  export const validateAllStoredTokens = (): {
    hasValidRefresh: boolean;
    hasValidSSO: boolean;
    hasAnyValid: boolean;
    refreshToken?: string;
    ssoToken?: string;
  } => {
    console.log("🔍 Validating all stored tokens...");
    
    let hasValidRefresh = false;
    let hasValidSSO = false;
    let refreshToken: string | undefined;
    let ssoToken: string | undefined;
    
    // 1. Validate refresh token from PERSIST_OBJECT
    const persistObject = localStorage.getItem("PERSIST_OBJECT");
    const useRefreshFlag = localStorage.getItem("useRefresh");
    
    if (persistObject && useRefreshFlag) {
      try {
        const parsed = JSON.parse(persistObject);
        refreshToken = parsed.refresh_token || parsed.refreshToken;
        
        if (refreshToken && isTokenValid(refreshToken, 5)) {
          hasValidRefresh = true;
          console.log("✅ Valid refresh token found");
        } else {
          console.log("❌ Invalid/expired refresh token - clearing storage");
          localStorage.removeItem("PERSIST_OBJECT");
          localStorage.removeItem("useRefresh");
          refreshToken = undefined;
        }
      } catch (error) {
        console.log("❌ Error parsing PERSIST_OBJECT - clearing");
        localStorage.removeItem("PERSIST_OBJECT");
        localStorage.removeItem("useRefresh");
      }
    }
    
    // 2. Validate ssoToken from AUTH_LOGIN
    const authLogin = localStorage.getItem("AUTH_LOGIN");
    
    if (authLogin) {
      try {
        const parsed = JSON.parse(authLogin);
        ssoToken = parsed.ssoToken;
        
        if (ssoToken && isTokenValid(ssoToken, 1)) { // Smaller buffer for SSO token
          hasValidSSO = true;
          console.log("✅ Valid ssoToken found");
        } else {
          console.log("❌ Invalid/expired ssoToken - clearing AUTH_LOGIN");
          localStorage.removeItem("AUTH_LOGIN");
          ssoToken = undefined;
        }
      } catch (error) {
        console.log("❌ Error parsing AUTH_LOGIN - clearing");
        localStorage.removeItem("AUTH_LOGIN");
      }
    }
    
    const hasAnyValid = hasValidRefresh || hasValidSSO;
    
    console.log("=== TOKEN VALIDATION SUMMARY ===");
    console.log("Valid refresh token:", hasValidRefresh);
    console.log("Valid SSO token:", hasValidSSO);
    console.log("Has any valid auth:", hasAnyValid);
    console.log("===============================");
    
    return {
      hasValidRefresh,
      hasValidSSO,
      hasAnyValid,
      refreshToken: hasValidRefresh ? refreshToken : undefined,
      ssoToken: hasValidSSO ? ssoToken : undefined
    };
  };
  
  // Clear all invalid authentication data
  export const clearInvalidAuthData = (): void => {
    console.log("🧹 Clearing all invalid authentication data");
    
    localStorage.removeItem("PERSIST_OBJECT");
    localStorage.removeItem("AUTH_LOGIN");
    localStorage.removeItem("useRefresh");
    localStorage.removeItem("TV_ACS_TOKEN");
    
    // Clear session storage too
    sessionStorage.removeItem("MPIN_AUTH_SESSION");
    sessionStorage.removeItem("MPIN_AUTHENTICATED");
    sessionStorage.removeItem("COMING_FROM_MPIN");
    sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");
    sessionStorage.removeItem("MPIN_COMPLETED");
  };
  
  // Check if refresh token is specifically valid (for API calls)
  export const isRefreshTokenReadyForAPI = (): boolean => {
    const result = validateAllStoredTokens();
    return result.hasValidRefresh;
  };
  
  // Get token info with expiry details
  export const getTokenInfo = (token: string): {
    isValid: boolean;
    expiresAt?: Date;
    timeUntilExpiry?: number; // minutes
  } => {
    if (!token) {
      return { isValid: false };
    }
    
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
      return { isValid: false };
    }
    
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = new Date(payload.exp * 1000);
    const timeUntilExpiry = Math.floor((payload.exp - now) / 60); // minutes
    const isValid = now < payload.exp;
    
    return {
      isValid,
      expiresAt,
      timeUntilExpiry
    };
  };
  
  // Usage example in your components:
  /*
  import { validateAllStoredTokens, clearInvalidAuthData, isRefreshTokenReadyForAPI } from './utils/tokenValidation';
  
  // In AppRoutes or LoginForm:
  const authStatus = validateAllStoredTokens();
  if (!authStatus.hasAnyValid) {
    // Redirect to login
    navigate("/");
  }
  
  // In MpinInput before API call:
  if (!isRefreshTokenReadyForAPI()) {
    toast.error("Session expired. Please login again.");
    clearInvalidAuthData();
    navigate("/");
    return;
  }
  */